/**
 * "Replacement" on a resident (2026-10-21; Isaiah Cotto: "Any office member can file a replacement"). Shown inside the
 * UMED case panel only (it draws with umed.css, whose colours are scoped to `.um`). It asks the server (GET /replacement-requests/residents/:id)
 * whether this person may file for this resident, and says what is already open or just placed; it files
 * (POST) and withdraws (POST .../cancel) and shows nothing as done until the server confirms.
 *
 * Nothing is drawn for a resident outside the caller's reach (404, the same answer as no such resident) or a caller
 * without the right (403). A refusal is the server's `message`, as written. A lost answer keeps the attempt's
 * idempotency key, so trying again cannot file it twice; a refusal recorded nothing, so the next attempt gets a new key.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  replacementApi, newReplacementKey, dollarsToCents, etDay, SIDE_OPTIONS, REASON_OPTIONS, PATH_OPTIONS, NOTE_MAX,
} from './replacementRequestsApi';
import type { ReplacementStatus, ReplacementSide, ReplacementReason, ReplacementFileBody } from './replacementRequestsApi';

export interface ReplacementRequestPanelProps {
  patientId: string;
  residentName: string;
  /** Called with a sentence after the server confirmed a filing or a withdrawal, so the screen around it can read again. */
  onChanged?: (msg: string) => void;
  className?: string;
}

type Phase = 'closed' | 'open' | 'sending' | 'sent';
type Read = { kind: 'loading' } | { kind: 'hidden' } | { kind: 'failed' } | { kind: 'ready'; status: ReplacementStatus };

const LOST_ANSWER = 'eMerge did not confirm the request. Nothing is shown as sent. Try again; trying again will not send it twice.';
const LOST_WITHDRAW = 'eMerge did not confirm the withdrawal. The request is still open. Try again.';
const SENT_WORDS = 'Replacement requested. It is waiting on the Replacements desk; the facility’s Patient Advocate, the PA managers and the people who place replacements have been told.';

/** A lost answer: the server was never heard from, so nothing is known to be recorded. */
const lost = (r: { status?: number; code?: string }): boolean => r.status === undefined && !r.code;

export default function ReplacementRequestPanel({ patientId, residentName, onChanged, className }: ReplacementRequestPanelProps) {
  const [read, setRead] = useState<Read>({ kind: 'loading' });
  const [phase, setPhase] = useState<Phase>('closed');
  const [side, setSide] = useState<ReplacementSide | ''>('');
  const [reason, setReason] = useState<ReplacementReason | ''>('');
  const [note, setNote] = useState('');
  const [path, setPath] = useState('REVIEW_REQUIRED');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [key, setKey] = useState(newReplacementKey);
  const [withdrawing, setWithdrawing] = useState<'closed' | 'open' | 'sending'>('closed');
  const [why, setWhy] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawn, setWithdrawn] = useState('');
  const seq = useRef(0);
  const uid = useRef(`rr-${Math.random().toString(36).slice(2, 8)}`).current;

  const load = useCallback(async () => {
    const mine = ++seq.current;
    const r = await replacementApi.status(patientId);
    if (mine !== seq.current) return;
    if (r.success && r.data) setRead({ kind: 'ready', status: r.data });
    else if (r.status === 403 || r.status === 404) setRead({ kind: 'hidden' });
    else setRead({ kind: 'failed' });
  }, [patientId]);
  useEffect(() => {
    setRead({ kind: 'loading' }); setPhase('closed'); setSide(''); setReason(''); setNote(''); setPath('REVIEW_REQUIRED'); setAmount('');
    setError(''); setKey(newReplacementKey()); setWithdrawing('closed'); setWhy(''); setWithdrawError(''); setWithdrawn('');
    load();
    return () => { seq.current += 1; };
  }, [load]);

  if (read.kind === 'loading' || read.kind === 'hidden') return null;
  const cls = ['um-section', className].filter(Boolean).join(' ');
  if (read.kind === 'failed') {
    return (
      <div className={cls}>
        <h3>Replacement</h3>
        <p className="um-quiet">Could not check replacement requests for this resident.</p>
      </div>
    );
  }
  const st = read.status;
  const choosePath = st.may_choose_path;
  const noteNeeded = reason === 'OTHER' && !note.trim();
  const amountCents = amount.trim() ? dollarsToCents(amount) : null;
  const amountBad = Boolean(amount.trim()) && amountCents === null;
  const ready = side !== '' && reason !== '' && !noteNeeded && note.length <= NOTE_MAX && !(choosePath && amountBad);
  const sending = phase === 'sending';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || sending) return;
    setPhase('sending'); setError('');
    const body: ReplacementFileBody = { side: side as ReplacementSide, reason_code: reason as ReplacementReason, note: note.trim() || null, idempotency_key: key };
    if (choosePath) {
      body.financial_path = path;
      if (amountCents !== null) body.requested_amount_cents = amountCents;
    }
    const r = await replacementApi.file(patientId, body);
    if (r.success) {
      setPhase('sent');
      setKey(newReplacementKey());
      onChanged?.(`Replacement requested for ${residentName}.`);
      load();
      return;
    }
    if (lost(r)) {
      // Keep the key: if the first attempt did land, the retry is recognised as the same request.
      setError(LOST_ANSWER);
    } else {
      setError(r.message || r.error || 'The request was not recorded. Tell the office.');
      // A refusal recorded nothing; the next attempt is a new one.
      setKey(newReplacementKey());
      // Something may have changed under the form (someone else filed): read the resident again.
      if (r.code === 'REQUEST_ALREADY_OPEN') load();
    }
    setPhase('open');
  };

  const withdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    const open = st.open;
    const reasonText = why.trim();
    if (!open || withdrawing === 'sending' || reasonText.length < 3 || reasonText.length > 500) return;
    setWithdrawing('sending'); setWithdrawError('');
    const r = await replacementApi.cancel(open.id, reasonText);
    if (r.success) {
      setWithdrawing('closed'); setWhy(''); setPhase('closed');
      setWithdrawn('Your replacement request was withdrawn.');
      onChanged?.(`Replacement request withdrawn for ${residentName}.`);
      load();
      return;
    }
    setWithdrawError(lost(r) ? LOST_WITHDRAW : (r.message || r.error || 'The request was not withdrawn.'));
    setWithdrawing('open');
  };

  const sentNote = phase === 'sent' ? <p className="um-notice" role="status">{SENT_WORDS}</p> : null;

  return (
    <div className={cls}>
      <h3>Replacement</h3>
      {sentNote}
      {withdrawn && !st.open && <p className="um-notice" role="status">{withdrawn}</p>}

      {st.open && (
        <>
          <p>
            Replacement requested: <strong>{st.open.reason_words}</strong>
            {` — ${SIDE_OPTIONS.find((s) => s.value === st.open!.side)?.label || st.open.side} ear`}.{' '}
            Requested on {etDay(st.open.requested_at) || 'a date not recorded'}{st.open.requested_by ? ` by ${st.open.requested_by}` : ''}.
            {st.open.mine ? ' You asked for this.' : ''}
          </p>
          <p className="um-quiet">It is waiting on the Replacements desk. <a href={st.desk_href}>Open the Replacements desk</a></p>
          {st.open.mine && withdrawing === 'closed' && (
            <div>
              <button type="button" className="um-open" onClick={() => { setWithdrawing('open'); setWithdrawError(''); }}>Withdraw my request</button>
            </div>
          )}
          {st.open.mine && withdrawing !== 'closed' && (
            <form onSubmit={withdraw} aria-label="Withdraw my replacement request">
              <label className="um-rowfield">
                <span>Why are you withdrawing it? (3 to 500 characters)</span>
                <input value={why} maxLength={500} disabled={withdrawing === 'sending'} onChange={(e) => setWhy(e.target.value)} />
              </label>
              {withdrawError && <p className="um-notice stop" role="alert">{withdrawError}</p>}
              <div>
                <button type="submit" className="um-open um-primary" disabled={withdrawing === 'sending' || why.trim().length < 3}>
                  {withdrawing === 'sending' ? 'Withdrawing…' : 'Withdraw request'}
                </button>{' '}
                <button type="button" className="um-link" disabled={withdrawing === 'sending'} onClick={() => { setWithdrawing('closed'); setWithdrawError(''); }}>Keep it</button>
              </div>
            </form>
          )}
        </>
      )}

      {!st.open && st.placed_recently && (
        <p>
          A replacement was placed with {st.placed_recently.pa || 'the Patient Advocate'} on {etDay(st.placed_recently.placed_at) || 'a date not recorded'}.
          {' '}A new request can be made after {st.window_days} days, or ask the office.
        </p>
      )}

      {!st.open && !st.placed_recently && st.refusal && <p className="um-quiet">{st.refusal.message}</p>}

      {!st.open && !st.placed_recently && !st.refusal && st.may_file && phase !== 'open' && phase !== 'sending' && phase !== 'sent' && (
        <div>
          <button type="button" className="um-open" aria-label={`Request replacement for ${residentName}`}
            onClick={() => { setError(''); setPhase('open'); }}>Request replacement</button>
        </div>
      )}

      {!st.open && !st.placed_recently && !st.refusal && st.may_file && (phase === 'open' || phase === 'sending') && (
        <form onSubmit={submit} aria-label="Request a hearing-aid replacement">
          <fieldset disabled={sending} style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
            <legend>Which ear</legend>
            {SIDE_OPTIONS.map((s) => (
              <label key={s.value} style={{ marginRight: 14 }}>
                <input type="radio" name={`${uid}-side`} value={s.value} checked={side === s.value} onChange={() => setSide(s.value)} /> {s.label}
              </label>
            ))}
          </fieldset>
          <label className="um-rowfield">
            <span>Why</span>
            <select value={reason} disabled={sending} onChange={(e) => setReason(e.target.value as ReplacementReason | '')} aria-label="Why">
              <option value="">Choose a reason</option>
              {REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="um-rowfield">
            <span>Note {reason === 'OTHER' ? '(required for Other)' : '(optional)'}</span>
            <textarea rows={3} value={note} maxLength={NOTE_MAX} readOnly={sending} onChange={(e) => setNote(e.target.value)}
              aria-label="Note" aria-describedby={`${uid}-note-help`} />
          </label>
          <p id={`${uid}-note-help`} className="um-quiet">The office reads this note in eMerge. It is not emailed.</p>
          {choosePath && (
            <>
              <label className="um-rowfield">
                <span>How it is paid for</span>
                <select value={path} disabled={sending} onChange={(e) => setPath(e.target.value)} aria-label="How it is paid for">
                  {PATH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="um-rowfield">
                <span>Amount ($, optional)</span>
                <input value={amount} inputMode="decimal" readOnly={sending} onChange={(e) => setAmount(e.target.value)} aria-label="Amount ($, optional)" />
              </label>
              {amountBad && <p className="um-quiet">Type the amount in dollars, like 450 or 450.50.</p>}
            </>
          )}
          {error && <p className="um-notice stop" role="alert">{error}</p>}
          <div>
            <button type="submit" className="um-open um-primary" disabled={!ready || sending}>{sending ? 'Sending…' : 'Send request'}</button>{' '}
            <button type="button" className="um-link" disabled={sending} onClick={() => { setPhase('closed'); setError(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
